// src/components/Navbar.jsx — top navigation, mobile-first (hamburger on small screens)
import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import LanguageSwitcher from "./LanguageSwitcher";
import { Menu, Moon, Sun, X } from "lucide-react";

const Navbar = ({ theme = "sunrise", onThemeChange }) => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const publicLinks = [
    { to: "/", label: t("nav.home") },
    { to: "/circles", label: t("nav.circles") },
    { to: "/pricing", label: t("nav.pricing") },
    { to: "/join", label: t("nav.join") },
    { to: "/help", label: t("nav.help") },
  ];
  const memberLinks = [
    { to: "/dashboard", label: t("nav.dashboardLink") },
    { to: "/circles", label: t("nav.circlesLink") },
    { to: "/referral", label: t("nav.rewards") },
  ];
  const adminLinks = [
    { to: "/admin", label: t("nav.overview") },
    { to: "/admin?section=members", label: t("nav.members") },
    { to: "/admin?section=kyc", label: t("nav.kycQueue") },
    { to: "/admin?section=circles", label: t("nav.circlesLink") },
    { to: "/admin?section=transactions", label: t("nav.transactionsDisputes") },
    { to: "/admin?section=gdpr", label: t("nav.gdprRequests") },
    { to: "/admin?section=referrals", label: t("nav.referralReview") },
    { to: "/admin?section=faq", label: t("nav.faqContent") },
  ];
  const navLinks = user?.role === "admin" ? adminLinks : user ? memberLinks : publicLinks;
  const isActive = (to) => {
    const [pathname, query] = to.split("?");
    return (location.pathname === pathname
      && (query ? location.search === `?${query}` : !location.search))
      || location.pathname.startsWith(`${pathname}/`);
  };
  const linkClass = (to) =>
    `rounded-lg px-3 py-2 text-sm font-semibold transition ${
      isActive(to) ? "bg-gold-50 text-gold-700" : "text-gray-600 hover:bg-gold-50 hover:text-gold-700"
    }`;

  return (
    <nav className="sticky top-0 z-20 border-b border-gold-100 bg-white/95 shadow-sm backdrop-blur">
      <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
        <Link to={user ? (user.role === "admin" ? "/admin" : "/dashboard") : "/"} className="font-serif text-2xl font-bold tracking-tight text-gold-700">
          <span className="italic">i</span>Tontine
        </Link>

        {/* Desktop links */}
        <div className="hidden min-w-0 flex-1 items-center justify-end gap-1 md:flex">
          {user?.role === "admin" ? (
            <details className="relative mr-2 shrink-0">
              <summary className="cursor-pointer list-none rounded-lg px-3 py-2 text-sm font-semibold text-gray-600 transition hover:bg-gold-50 hover:text-gold-700">
                {t("nav.adminSections")}
              </summary>
              <div className="absolute right-0 top-full z-30 mt-2 flex w-64 flex-col gap-1 rounded-xl border border-gold-100 bg-white p-2 shadow-lg">
                {adminLinks.map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    onClick={(event) => { event.currentTarget.closest("details").open = false; }}
                    className={linkClass(link.to)}
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            </details>
          ) : (
            <div className="flex min-w-0 items-center gap-1 overflow-x-auto whitespace-nowrap [scrollbar-width:none]">
              {navLinks.map((link) => (
                <Link key={link.to} to={link.to} className={`${linkClass(link.to)} shrink-0`}>
                  {link.label}
                </Link>
              ))}
            </div>
          )}
          <div className="shrink-0"><LanguageSwitcher /></div>
          <button type="button" onClick={onThemeChange} className="ml-1 shrink-0 rounded-xl p-2 text-gray-600 transition hover:bg-gold-50 hover:text-gold-700" aria-label={t(theme === "sunrise" ? "nav.switchThemeDark" : "nav.switchThemeLight")}>
            {theme === "sunrise" ? <Moon size={17} /> : <Sun size={17} />}
          </button>
          {user ? <button onClick={handleLogout} className="ml-2 shrink-0 whitespace-nowrap rounded-xl px-3 py-2 text-sm font-semibold text-gray-600 transition-all hover:bg-gold-50 hover:text-gold-700">{t("nav.logout")}</button> : <div className="ml-2 flex shrink-0 items-center gap-3 whitespace-nowrap"><Link to="/login" className="text-sm font-semibold text-gray-600 hover:text-gold-700">{t("nav.login")}</Link><Link to="/join" className="rounded-xl bg-gold-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gold-700">{t("nav.register")}</Link></div>}
        </div>

        {/* Mobile hamburger */}
        <button
          className="rounded-lg p-2 text-gold-700 transition-all hover:bg-gold-50 md:hidden"
          onClick={() => setMenuOpen((o) => !o)}
          aria-label={t("nav.toggleMenu")}
        >
          {menuOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="flex flex-col gap-2 border-t border-gold-100 px-4 pb-4 pt-3 md:hidden">
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              onClick={() => setMenuOpen(false)}
              className={linkClass(link.to)}
            >
              {link.label}
            </Link>
          ))}
          <LanguageSwitcher />
          <button type="button" onClick={onThemeChange} className="flex items-center gap-2 rounded-xl px-3 py-2 text-left text-sm font-semibold text-gray-600 hover:bg-gold-50 hover:text-gold-700">
            {theme === "sunrise" ? <Moon size={17} /> : <Sun size={17} />}
            {t(theme === "sunrise" ? "nav.themeDark" : "nav.themeLight")}
          </button>
          {user ? (
            <button onClick={handleLogout} className="text-sm text-left text-gray-600">
              {t("nav.logout")}
            </button>
          ) : (
            <div className="flex items-center gap-3 pt-2">
              <Link to="/login" onClick={() => setMenuOpen(false)} className="text-sm font-semibold text-gray-600">{t("nav.login")}</Link>
              <Link to="/join" onClick={() => setMenuOpen(false)} className="rounded-xl bg-gold-600 px-4 py-2 text-sm font-semibold text-white">{t("nav.register")}</Link>
            </div>
          )}
        </div>
      )}
    </nav>
  );
};

export default Navbar;
